import React, { useState, useEffect, useRef } from 'react';

interface Command {
  id: string;
  label: string;
  icon: string;
  description?: string;
  action: () => void;
}

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (tabId: string) => void;
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ isOpen, onClose, onNavigate }) => {
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const commands: Command[] = [
    {
      id: 'discover',
      label: 'Discover New Suppliers',
      icon: '🔎',
      description: 'AI-powered supplier discovery',
      action: () => onNavigate('discover')
    },
    {
      id: 'suppliers',
      label: 'View Suppliers',
      icon: '🏢',
      description: 'Manage supplier database',
      action: () => onNavigate('suppliers')
    },
    {
      id: 'contacts',
      label: 'View Contacts',
      icon: '👥',
      description: 'Supplier contact directory',
      action: () => onNavigate('contacts')
    },
    {
      id: 'quotes',
      label: 'View Quotes',
      icon: '💰',
      description: 'Quote pipeline management',
      action: () => onNavigate('quotes')
    },
    {
      id: 'solutions',
      label: 'View Solutions',
      icon: '📦',
      description: 'Product catalog & templates',
      action: () => onNavigate('solutions')
    },
    {
      id: 'analytics',
      label: 'View Analytics',
      icon: '📊',
      description: 'Supplier operations insights',
      action: () => onNavigate('analytics')
    }
  ];

  const filteredCommands = commands.filter(cmd =>
    cmd.label.toLowerCase().includes(search.toLowerCase()) ||
    cmd.description?.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setSearch('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => Math.min(prev + 1, filteredCommands.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredCommands[selectedIndex]) {
            filteredCommands[selectedIndex].action();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, filteredCommands]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity" onClick={onClose}></div>

      {/* Command Palette */}
      <div className="flex min-h-screen items-start justify-center p-4 pt-[20vh]">
        <div className="relative w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200 transition-all">
          {/* Search Input */}
          <div className="border-b border-gray-200 p-4">
            <input
              ref={inputRef}
              type="text"
              className="w-full text-lg outline-none placeholder:text-gray-400"
              placeholder="Type a command or search..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSelectedIndex(0);
              }}
            />
          </div>

          {/* Commands List */}
          <div className="max-h-96 overflow-y-auto p-2">
            {filteredCommands.length === 0 ? (
              <div className="py-10 text-center text-sm text-gray-500">
                No commands found
              </div>
            ) : (
              <div className="space-y-1">
                {filteredCommands.map((cmd, index) => (
                  <button
                    key={cmd.id}
                    onClick={cmd.action}
                    className={`
                      w-full rounded-lg px-4 py-3 text-left transition
                      ${index === selectedIndex
                        ? 'bg-primary text-white'
                        : 'text-gray-900 hover:bg-gray-100'
                      }
                    `}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{cmd.icon}</span>
                      <div className="flex-1">
                        <div className={`font-medium ${index === selectedIndex ? 'text-white' : 'text-gray-900'}`}>
                          {cmd.label}
                        </div>
                        {cmd.description && (
                          <div className={`text-sm ${index === selectedIndex ? 'text-white/80' : 'text-gray-500'}`}>
                            {cmd.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-500">
            <div className="flex items-center justify-between">
              <span>Navigate with ↑↓, select with ⏎</span>
              <span>Press ESC to close</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
